<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'manage',
    subject: 'Wrapup',
  },
})

const { t } = useI18n()
const { showToast } = useToast()

interface WrapupCategory {
  id: number
  name: string
  description?: string
  path: string
  level: number
  isActive: boolean
  sortOrder: number
  groupStatusId?: number
  parentId?: number
  children?: WrapupCategory[]
  groupStatus?: {
    id: number
    name: string
    color?: string
    icon?: string
  }
}

// Main State
const categories = ref<WrapupCategory[]>([])
const parentOptions = ref<WrapupCategory[]>([]) // สำหรับเลือก parent ที่ทุก level
const selectedCategory = ref<WrapupCategory | null>(null)
const loading = ref(false)
const saving = ref(false)
const groupStatuses = ref<any[]>([])

// Computed property for parent options - ต้องกรอง เพื่อไม่ให้เลือกตัวเองเป็น parent สำหรับ main form
const filteredParentOptions = computed(() => {
  if (!selectedCategory.value)
    return parentOptions.value

  // เมื่อแก้ไข category กรองตัวเองออกจาก parent options
  return parentOptions.value.filter(option => option.id !== selectedCategory.value?.id)
})

// Computed property for Dialog parent options
const dialogParentOptions = computed(() => {
  // สำหรับ dialog create/child ส่งคืน parentOptions ธรรมดา
  return parentOptions.value
})

// Form data (สำหรับ main form ด้านขวา)
const formData = ref({
  name: '',
  description: '',
  isActive: true,
  sortOrder: 0,
  groupStatusId: null as number | null,
  parentId: null as number | null,
})

// Dialog form data (สำหรับ dialog สร้างใหม่/แก้ไข)
const dialogFormData = ref({
  name: '',
  description: '',
  isActive: true,
  sortOrder: 0,
  groupStatusId: null as number | null,
  parentId: null as number | null,
})

// Dialog states
const showDialog = ref(false)

// Tree view options for VTreeview
const treeViewOpen = ref<string[]>([])
const treeViewActive = ref<string[]>([])
const treeViewItems = ref<any[]>([])

// Search, filter, and pagination state
const searchQuery = ref('')
const statusFilter = ref<boolean | null>(null)
const currentPage = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)
const totalPages = ref(0)

// Convert data to VTreeview format
const convertToTreeNode = (item: WrapupCategory): any => ({
  id: item.id.toString(),
  name: item.name,
  children: item.children ? item.children.map(convertToTreeNode) : [],
  item, // Store original item
})

// Methods
const fetchTreeData = async () => {
  loading.value = true

  try {
    const params = new URLSearchParams()

    params.append('page', currentPage.value.toString())
    params.append('itemsPerPage', itemsPerPage.value.toString())

    if (searchQuery.value.trim())
      params.append('q', searchQuery.value.trim())

    if (statusFilter.value !== null)
      params.append('isActive', statusFilter.value.toString())

    const { data } = await useApi(`/wrapup/tree?${params.toString()}`)

    if (data.value) {
      if (Array.isArray(data.value.data))
        categories.value = data.value.data
      else if (Array.isArray(data.value))
        categories.value = data.value
      else
        categories.value = []

      totalItems.value = data.value?.total || 0
      totalPages.value = data.value?.totalPages || 0

      treeViewItems.value = categories.value.map(convertToTreeNode)
    }
    else {
      categories.value = []
      treeViewItems.value = []
    }
  }
  catch (error) {
    console.error('Failed to fetch tree:', error)
    showToast({
      type: 'error',
      message: t('wrapup.error'),
    })
    categories.value = []
    treeViewItems.value = []
    totalItems.value = 0
    totalPages.value = 0
  }
  finally {
    loading.value = false
  }
}

const fetchGroupStatuses = async () => {
  try {
    // Use active-non-system endpoint to exclude system statuses (like NEW)
    const { data } = await useApi('/wrapup-group-status/active-non-system')

    groupStatuses.value = Array.isArray(data.value) ? data.value : []
  }
  catch (error) {
    console.error('Failed to fetch group statuses:', error)
    showToast({
      type: 'error',
      message: t('wrapup.error'),
    })
  }
}

// Handle tree node update (lazy loading)
const loadChildren = async (item: any) => {
  try {
    // Get the actual wrapper item data
    const actualItem = item.item || item


    const { data } = await useApi(`/wrapup/${actualItem.id}/children`)
    const children = data.value || []


    if (children.length > 0) {
      // Convert children to tree format
      const childrenTree = children.map(convertToTreeNode)


      // Find and update the correct parent node recursively
      const found = findAndUpdateChildren(treeViewItems.value, actualItem.id, childrenTree)


      if (found) {
        // Force reactivity update
        treeViewItems.value = [...treeViewItems.value]
      }
    }

    return children
  }
  catch (error) {
    console.error('Failed to load children:', error)

    return []
  }
}

// Find and update children in tree recursively using the original item ID
const findAndUpdateChildren = (items: any[], targetId: number, children: any[]): boolean => {
  for (const item of items) {
    // Check both string ID and stored item ID
    const stringTargetId = targetId.toString()

    const isIdMatch = item.id === stringTargetId
                     || item.item?.id === targetId
                     || (item.id && Number.parseInt(item.id) === targetId)

    if (isIdMatch) {
      item.children = children

      return true
    }

    // Recursively search through children
    if (item.children && item.children.length > 0) {
      if (findAndUpdateChildren(item.children, targetId, children))
        return true
    }
  }

  return false
}

// Update tree items with children recursively
const updateTreeItems = (items: any[], targetId: string, children: any[]) => {
  for (const item of items) {
    if (item.id === targetId) {
      item.children = children

      return true
    }
    if (item.children && item.children.length > 0) {
      if (updateTreeItems(item.children, targetId, children))
        return true
    }
  }

  return false
}

// Helper to find tree item by id (includes children recursively)
const findTreeItemById = (items: any[], id: string): any => {
  for (const item of items) {
    if (item.id === id)
      return item

    if (item.children) {
      const found = findTreeItemById(item.children, id)

      if (found)
        return found
    }
  }

  return null
}

// Update tree when expanded
const updateTree = (itemId: string) => {
  const item = treeViewItems.value.find(i => i.id === itemId)

  if (item && !item.children)
    loadChildren(item)
}

const onNodeExpand = (nodeIds?: string[]) => {
  if (!nodeIds)
    return

  // Find newly expanded items
  const newOpen = nodeIds.filter(id => !treeViewOpen.value.includes(id))

  newOpen.forEach(id => updateTree(id))
  treeViewOpen.value = [...nodeIds]
}

const selectCategory = (category: WrapupCategory) => {
  selectedCategory.value = category
  formData.value = {
    name: category.name,
    description: category.description || '',
    isActive: category.isActive,
    sortOrder: category.sortOrder,
    groupStatusId: category.groupStatusId || null,
    parentId: category.parentId || null,
  }
}

const onNodeSelect = (nodeIds?: string[]) => {
  if (!nodeIds)
    return

  // Select the latest clicked item
  const nodeId = nodeIds[nodeIds.length - 1] || ''
  const item = findTreeItemById(treeViewItems.value, nodeId)

  if (item && item.item)
    selectCategory(item.item)

  treeViewActive.value = [...nodeIds]
}

const openCreateDialog = (parentCategory?: WrapupCategory) => {
  selectedCategory.value = parentCategory || null
  dialogFormData.value = {
    name: '',
    description: '',
    isActive: true,
    sortOrder: 0,
    groupStatusId: null,
    parentId: parentCategory ? parentCategory.id : null,
  }
  showDialog.value = true
}

const saveCategory = async () => {
  // Main form save (สำหรับ tree selection)
  if (!formData.value.name.trim()) {
    showToast({
      type: 'error',
      message: 'กรุณากรอกชื่อหมวดหมู่',
    })

    return
  }

  saving.value = true

  try {
    if (selectedCategory.value) {
      // Update existing from main form
      await $api(`/wrapup/${selectedCategory.value.id}`, {
        method: 'PATCH',
        body: {
          name: formData.value.name,
          description: formData.value.description,
          isActive: formData.value.isActive,
          sortOrder: formData.value.sortOrder,
          groupStatusId: formData.value.groupStatusId,
          parentId: formData.value.parentId,
        },
      })
      showToast({
        type: 'success',
        message: t('wrapup.wrapupUpdatedSuccessfully'),
      })
    }
    await fetchTreeData()
  }
  catch (error) {
    console.error('Failed to save wrapup:', error)
    showToast({
      type: 'error',
      message: t('wrapup.error'),
    })
  }
  finally {
    saving.value = false
  }
}

const saveDialogCategory = async () => {
  // Dialog save (สำหรับ create new/child เท่านั้น)
  if (!dialogFormData.value.name.trim()) {
    showToast({
      type: 'error',
      message: 'กรุณากรอกชื่อหมวดหมู่',
    })

    return
  }

  saving.value = true

  try {
    // Create new (root or child)
    const requestBody: any = {
      name: dialogFormData.value.name,
      description: dialogFormData.value.description,
      isActive: dialogFormData.value.isActive,
      sortOrder: dialogFormData.value.sortOrder,
      groupStatusId: dialogFormData.value.groupStatusId,
    }

    if (dialogFormData.value.parentId) {
      // Create as child (parent is set via dropdown)
      requestBody.parentId = dialogFormData.value.parentId

      // Find parent level to set current item level
      const parent = parentOptions.value.find(c => c.id === dialogFormData.value.parentId)

      requestBody.level = parent ? parent.level + 1 : 1

      // Check if parent category was automatically selected
      if (selectedCategory.value && selectedCategory.value.id === dialogFormData.value.parentId) {
        showToast({
          type: 'success',
          message: 'สร้างลูกหมวดหมู่สำเร็จ - ลูกได้ถูกโหลดใหม่อัตโนมัติ',
        })
      }
      else {
        showToast({
          type: 'success',
          message: t('wrapup.createChildMessage'),
        })
      }
    }
    else {
      // Create as root
      requestBody.level = 0
      showToast({
        type: 'success',
        message: t('wrapup.wrapupCreatedSuccessfully'),
      })
    }

    await $api('/wrapup', {
      method: 'POST',
      body: requestBody,
    })

    showDialog.value = false
    await fetchTreeData()
    await fetchParentOptions() // Refresh parent options after creating
  }
  catch (error) {
    console.error('Failed to save wrapup:', error)
    showToast({
      type: 'error',
      message: t('wrapup.error'),
    })
  }
  finally {
    saving.value = false
  }
}

// Search and filter methods
const onSearchInput = async () => {
  currentPage.value = 1
  await fetchTreeData()
}

const onFilterChange = async () => {
  currentPage.value = 1
  await fetchTreeData()
}

const onPageChange = async (page: number) => {
  currentPage.value = page
  await fetchTreeData()
}

const clearSearch = async () => {
  searchQuery.value = ''
  statusFilter.value = null
  currentPage.value = 1
  await fetchTreeData()
}

const fetchParentOptions = async () => {
  try {
    const { data } = await useApi('/wrapup/all-active')

    parentOptions.value = data.value
  }
  catch (error) {
    console.error('Failed to fetch parent options:', error)
  }
}

onMounted(async () => {
  await fetchGroupStatuses()
  await fetchParentOptions()
  await fetchTreeData()
})

// Refresh parent options
watch(() => showDialog.value, async newVal => {
  if (newVal)
    await fetchParentOptions()
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <div class="d-flex align-center justify-space-between">
        <div>
          <h1 class="text-h4 font-weight-bold mb-2">
            {{ t('wrapup.wrapupCategories') }}
          </h1>
          <p class="text-body-1 text-medium-emphasis">
            {{ t('wrapup.manageWrapupCategories') }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex gap-3">
          <VBtn
            v-if="selectedCategory"
            color="success"
            variant="outlined"
            @click="openCreateDialog(selectedCategory)"
          >
            <VIcon
              start
              icon="tabler-folder-plus"
            />
            {{ t('wrapup.createChild') }}
          </VBtn>

          <VBtn
            color="primary"
            @click="openCreateDialog"
          >
            <VIcon
              start
              icon="tabler-plus"
            />
            {{ t('wrapup.createNew') }}
          </VBtn>
        </div>
      </div>
    </div>

    <!-- 👉 Main Content - 2 Column Layout -->
    <VRow>
      <!-- Left Column - Tree View -->
      <VCol
        cols="12"
        lg="4"
        xl="3"
      >
        <VCard>
          <VCardTitle class="d-flex align-center">
            <VIcon
              icon="tabler-tree"
              class="me-2"
            />
            {{ t('wrapup.mainCategories') }}
          </VCardTitle>

          <VCardText>
            <!-- Search and Filter Section -->
            <div class="mb-4">
              <VRow>
                <VCol cols="12">
                  <VTextField
                    v-model="searchQuery"
                    :placeholder="t('wrapup.searchWrapupCategories')"
                    prepend-inner-icon="tabler-search"
                    clearable
                    hide-details="auto"
                    @update:model-value="onSearchInput"
                    @click:clear="clearSearch"
                  />
                </VCol>
                <VCol cols="12">
                  <VSelect
                    v-model="statusFilter"
                    :placeholder="t('wrapup.filterByStatus')"
                    :items="[
                      { title: t('common.showAll'), value: null },
                      { title: t('common.active'), value: true },
                      { title: t('common.inactive'), value: false },
                    ]"
                    item-title="title"
                    item-value="value"
                    clearable
                    hide-details="auto"
                    @update:model-value="onFilterChange"
                  />
                </VCol>
              </VRow>
            </div>

            <div v-if="loading">
              <div class="text-center py-4">
                <VProgressCircular indeterminate />
                <p class="text-sm mt-2">
                  {{ t('common.loading') }}
                </p>
              </div>
            </div>

            <div
              v-else-if="categories.length === 0"
              class="text-center py-4"
            >
              <VAvatar
                color="grey-lighten-2"
                size="40"
                class="mb-2"
              >
                <VIcon>tabler-folder-off</VIcon>
              </VAvatar>
              <p class="text-sm text-medium-emphasis">
                {{ t('wrapup.noCategoriesFound') }}
              </p>
            </div>

            <div
              v-else
              class="tree-container"
            >
              <VTreeview
                v-model:opened="treeViewOpen"
                v-model:activated="treeViewActive"
                expand-icon="tabler-chevron-right"
                collapse-icon="tabler-chevron-down"
                fluid
                border
                rounded
                activatable
                :items="treeViewItems"
                :load-children="loadChildren"
                open-strategy="single"
                class="tree-view"
                @update:opened="onNodeExpand"
                @update:activated="onNodeSelect"
              >
                <template #prepend="{ item }">
                  <VIcon
                    :color="item.item?.isActive ? 'success' : 'error'"
                    :icon="item.item?.isActive ? 'tabler-folder' : 'tabler-folder-off'"
                    size="20"
                    class="me-2"
                  />
                </template>

                <template #title="{ item }">
                  <div class="d-flex align-center">
                    <div
                      class="tree-node-content pt-0 pb-0"
                      @click="onNodeSelect([item.id])"
                    >
                      <h6 class="mb-0 text-sm font-weight-medium">
                        {{ item.item?.name || item.name }}
                      </h6>
                      <p
                        v-if="item.item?.description"
                        class="text-xs text-medium-emphasis mb-0"
                      >
                        {{ item.item.description }}
                      </p>
                      <p
                        v-else
                        class="text-xs text-medium-emphasis mb-0"
                      >
                        {{ t('common.noDescription') }}
                      </p>
                    </div>
                  </div>
                </template>
              </VTreeview>

              <!-- Pagination -->
              <div
                v-if="totalPages > 1"
                class="mt-4"
              >
                <VPagination
                  v-model="currentPage"
                  :length="totalPages"
                  :total-visible="5"
                  @update:model-value="onPageChange"
                />

                <div class="text-center text-sm text-medium-emphasis mt-2">
                  {{ t('common.showingResults') }} {{ ((currentPage - 1) * itemsPerPage) + 1 }}-{{ Math.min(currentPage * itemsPerPage, totalItems) }}
                  {{ t('common.ofResults') }} {{ totalItems }}
                </div>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Right Column - Form View -->
      <VCol
        cols="12"
        lg="8"
        xl="9"
      >
        <VCard>
          <VCardTitle class="d-flex align-center">
            <VIcon
              icon="tabler-forms"
              class="me-2"
            />
            {{ selectedCategory ? t('wrapup.categoryInfo') : t('wrapup.selectToEdit') }}
          </VCardTitle>

          <VCardText>
            <div
              v-if="!selectedCategory"
              class="text-center py-8"
            >
              <VAvatar
                color="grey-lighten-2"
                size="60"
                class="mb-3"
              >
                <VIcon>tabler-folder-search</VIcon>
              </VAvatar>
              <h6 class="text-h6 mb-2">
                {{ t('wrapup.selectCategoryToShow') }}
              </h6>
              <p class="text-sm text-medium-emphasis">
                {{ t('wrapup.clickToViewEdit') }}
              </p>
            </div>

            <VForm
              v-else
              @submit.prevent="saveCategory"
            >
              <VRow>
                <VCol
                  cols="12"
                  md="6"
                >
                  <VTextField
                    v-model="formData.name"
                    :label="t('wrapup.name')"
                    :placeholder="t('wrapup.enterName')"
                    required
                    :rules="[v => !!v || t('wrapup.pleaseEnterName')]"
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <VTextField
                    v-model.number="formData.sortOrder"
                    :label="t('wrapup.sortOrder')"
                    type="number"
                    placeholder="0"
                  />
                </VCol>

                <VCol cols="12">
                  <VTextarea
                    v-model="formData.description"
                    :label="t('wrapup.description')"
                    :placeholder="t('wrapup.enterDescription')"
                    rows="3"
                  />
                </VCol>

                <VCol cols="12">
                  <VSelect
                    v-model="formData.parentId"
                    :label="t('wrapup.parent')"
                    :items="filteredParentOptions"
                    item-title="name"
                    item-value="id"
                    :placeholder="t('wrapup.selectParent')"
                    clearable
                  />
                </VCol>

                <VCol cols="12">
                  <VSelect
                    v-model="formData.groupStatusId"
                    :label="t('wrapup.groupStatus')"
                    :items="groupStatuses"
                    item-title="name"
                    item-value="id"
                    :placeholder="t('wrapup.selectGroupStatus')"
                    clearable
                  >
                    <template #item="{ props, item }">
                      <VListItem v-bind="props">
                        <div class="d-flex align-center">
                          <VIcon
                            v-if="item.raw.icon"
                            :icon="item.raw.icon"
                            :color="item.raw.color"
                            size="20"
                            class="me-3"
                          />
                          <div>
                            <span>{{ item.raw.name }}</span>
                            <div
                              v-if="item.raw.description"
                              class="text-caption text-medium-emphasis"
                            >
                              {{ item.raw.description }}
                            </div>
                          </div>
                        </div>
                      </VListItem>
                    </template>
                  </VSelect>
                </VCol>

                <VCol cols="12">
                  <VSwitch
                    v-model="formData.isActive"
                    :label="t('common.status')"
                    color="success"
                    inset
                  />
                </VCol>

                <VCol
                  cols="12"
                  class="d-flex justify-end gap-3"
                >
                  <VBtn
                    color="error"
                    variant="outlined"
                    type="reset"
                  >
                    {{ t('common.cancel') }}
                  </VBtn>

                  <VBtn
                    color="primary"
                    type="submit"
                    :loading="saving"
                    :disabled="saving"
                  >
                    <VIcon icon="tabler-device-floppy" />
                    {{ t('common.save') }}
                  </VBtn>
                </VCol>
              </VRow>
            </VForm>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 👉 Create/Edit Dialog -->
    <VDialog
      v-model="showDialog"
      max-width="500px"
    >
      <VCard>
        <VCardTitle>
          {{
            selectedCategory
              ? t('wrapup.createChildIn', { name: selectedCategory.name })
              : t('wrapup.createNew')
          }}
        </VCardTitle>

        <VCardText>
          <div
            v-if="selectedCategory"
            class="mb-4"
          >
            <VAlert
              type="info"
              variant="tonal"
              class="mb-4"
            >
              <VIcon
                icon="tabler-info-circle"
                class="me-2"
              />
              {{ t('wrapup.createSubCategoryOf', { name: selectedCategory.name }) }}
            </VAlert>
          </div>

          <VForm @submit.prevent="saveDialogCategory">
            <VTextField
              v-model="dialogFormData.name"
              :label="t('wrapup.name')"
              :placeholder="t('wrapup.enterName')"
              required
              :rules="[v => !!v || t('wrapup.pleaseEnterName')]"
              class="mb-4"
            />

            <VTextarea
              v-model="dialogFormData.description"
              :label="t('wrapup.description')"
              :placeholder="t('wrapup.enterDescription')"
              rows="3"
              class="mb-4"
            />

            <VTextField
              v-model.number="dialogFormData.sortOrder"
              :label="t('wrapup.sortOrder')"
              type="number"
              placeholder="0"
              class="mb-4"
            />

            <VSelect
              v-model="dialogFormData.parentId"
              :label="t('wrapup.parent')"
              :items="dialogParentOptions"
              item-title="name"
              item-value="id"
              :placeholder="t('wrapup.selectParent')"
              clearable
              class="mb-4"
            />

            <VSelect
              v-model="dialogFormData.groupStatusId"
              :label="t('wrapup.groupStatus')"
              :items="groupStatuses"
              item-title="name"
              item-value="id"
              :placeholder="t('wrapup.selectGroupStatus')"
              clearable
              class="mb-4"
            >
              <template #item="{ props, item }">
                <VListItem v-bind="props">
                  <div class="d-flex align-center">
                    <VIcon
                      v-if="item.raw.icon"
                      :icon="item.raw.icon"
                      :color="item.raw.color"
                      size="20"
                      class="me-3"
                    />
                    <div>
                      <span>{{ item.raw.name }}</span>
                      <div
                        v-if="item.raw.description"
                        class="text-caption text-medium-emphasis"
                      >
                        {{ item.raw.description }}
                      </div>
                    </div>
                  </div>
                </VListItem>
              </template>
            </VSelect>

            <VSwitch
              v-model="dialogFormData.isActive"
              :label="t('common.status')"
              color="success"
              inset
            />
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            color="error"
            variant="outlined"
            @click="showDialog = false"
          >
            {{ t('common.cancel') }}
          </VBtn>

          <VBtn
            color="primary"
            :loading="saving"
            :disabled="saving"
            @click="saveDialogCategory"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style scoped>
.tree-container {
  max-height: 60vh;
  overflow-y: auto;
}

.tree-view {
  --v-treeview--color: rgb(var(--v-theme-primary));

  .v-treeview-item {
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.04);
    }

    &.v-treeview-item--active {
      background-color: rgba(var(--v-theme-primary), 0.08);
    }
  }

  .tree-node-content {
    width: 100%;
  }
}

.cursor-pointer {
  cursor: pointer;
}
</style>
 -replace ' +$', ''\n
